/**
 * ThemeOverrideRoot -- root component for the ThemeOverride integrated component.
 *
 * Supports two configuration modes:
 * - **Local mode**: `config` prop provides ThemeOverrideConfig directly
 * - **Remote mode**: `remoteConfigId` prop fetches config from Origami API
 *
 * Unlike DataList, ThemeOverride has no compound sub-components. The exported
 * `ThemeOverride` is simply the root component.
 *
 * @see types.ts `ThemeOverrideProps` for the full props interface.
 */

import * as React from 'react';
import { ThemeOverrideContext } from '../context/ThemeOverrideContext';
import { useThemeOverride } from '../hooks/useThemeOverride';
import { useRemoteComponentConfig } from '../../shared';
import { Spinner, Alert } from '@components/base/feedback';
import type { ThemeOverrideConfig, ThemeOverrideProps } from '../types';

// ---------------------------------------------------------------------------
// Props for the inner component (config is required)
// ---------------------------------------------------------------------------

interface ThemeOverrideLocalProps {
  config: ThemeOverrideConfig;
  as?: React.ElementType;
  className?: string;
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// ThemeOverrideLocal -- core themed scope with guaranteed config
// ---------------------------------------------------------------------------

function ThemeOverrideLocal({
  config,
  as: Component = 'div',
  className,
  children,
}: Readonly<ThemeOverrideLocalProps>): React.ReactElement {
  const { styleOverrides, config: resolvedConfig, activeOverrides } =
    useThemeOverride(config);

  const contextValue = React.useMemo(
    () => ({ config: resolvedConfig, activeOverrides }),
    [resolvedConfig, activeOverrides],
  );

  return (
    <ThemeOverrideContext.Provider value={contextValue}>
      {React.createElement(
        Component,
        {
          style: styleOverrides,
          className,
          'data-testid': 'theme-override-root',
        },
        children,
      )}
    </ThemeOverrideContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// ThemeOverrideRemote -- fetches config, then renders local
// ---------------------------------------------------------------------------

function ThemeOverrideRemote({
  remoteConfigId,
  tenant,
  module,
  loadingFallback,
  errorMessage,
  ...rest
}: Readonly<ThemeOverrideProps>): React.ReactElement {
  const { config, isLoading, error } =
    useRemoteComponentConfig<ThemeOverrideConfig>({
      componentId: remoteConfigId!,
      tenant,
      module,
      errorMessage,
    });

  if (isLoading) {
    return <>{loadingFallback ?? <Spinner />}</>;
  }

  if (error || !config) {
    return (
      <Alert variant="error">
        {error ?? 'Erreur de configuration du composant'}
      </Alert>
    );
  }

  return <ThemeOverrideLocal {...rest} config={config} />;
}

// ---------------------------------------------------------------------------
// ThemeOverrideRoot -- mode branching
// ---------------------------------------------------------------------------

function ThemeOverrideRoot(
  props: Readonly<ThemeOverrideProps>,
): React.ReactElement {
  const { config, remoteConfigId } = props;

  if (config && remoteConfigId) {
    throw new Error(
      'ThemeOverride: "config" and "remoteConfigId" are mutually exclusive. Provide one or the other, not both.',
    );
  }

  if (!config && !remoteConfigId) {
    throw new Error(
      'ThemeOverride: either "config" or "remoteConfigId" must be provided.',
    );
  }

  if (remoteConfigId) {
    return <ThemeOverrideRemote {...props} />;
  }

  // Local mode -- config is guaranteed here
  return (
    <ThemeOverrideLocal
      config={config!}
      as={props.as}
      className={props.className}
    >
      {props.children}
    </ThemeOverrideLocal>
  );
}

ThemeOverrideRoot.displayName = 'ThemeOverrideRoot';

// ---------------------------------------------------------------------------
// Export (no compound sub-components)
// ---------------------------------------------------------------------------

const ThemeOverride = ThemeOverrideRoot;

export { ThemeOverride };
